import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDoctorsByFacility, getRestrictedDates } from '../../../../../api/transports';
import useFetchList from '../../../../../hooks/useFetchList';
import { FIELDS, PICKUP_TYPE_HOME, TRIP_DIRECTION } from '../../constants';
import AdditionalDetails from '../AdditionalDetails';
import AppointmentDescription from '../AppointmentDescription';
import AppointmentSection from '../AppointmentSection';
import EscortDetails from '../EscortDetails';
import Location from '../Location';
import OutsourceSection from '../OutsourceSection';
import RecurrenceRules from '../RecurrenceRules';
import {
  getTransportContentSx,
  getTransportLabelSx,
  transportDetailsSectionSx,
  transportReadOnlySx,
} from '../transportTypographySx';
import { labelSort } from '../utils';

TransportDetailsTab.propTypes = {
  request: PropTypes.object,
  formState: PropTypes.object,
  setFields: PropTypes.func,
  editable: PropTypes.bool,
  setIsAllowedPickupDate: PropTypes.func,
  setIsAllowedAppointmentDate: PropTypes.func,
  wasRecurring: PropTypes.bool,
};

export default function TransportDetailsTab({
  request,
  formState,
  setFields,
  editable,
  setIsAllowedPickupDate,
  setIsAllowedAppointmentDate,
  wasRecurring,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const hasLinkedRequest = Boolean(request.linkedRequestId && request.direction);

  // Doctors
  const [doctorsRefetchTrigger, setDoctorsRefetchTrigger] = useState(0);
  const [doctorsList, doctorsLoading] = useFetchList(
    getDoctorsByFacility,
    [formState.FacilityId, doctorsRefetchTrigger],
    formState.FacilityId ? [formState.FacilityId] : [null],
    labelSort
  );

  // Restricted Dates
  const [restrictedDates, restrictedDatesLoading] = useFetchList(
    getRestrictedDates,
    [formState.FacilityId],
    formState.FacilityId ? [formState.FacilityId] : [null]
  );
  const setSingleField = (field, value) => setFields({ [field]: value });
  const locationTypeTooltip = editable ? 'Location type cannot be changed' : '';

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ ...transportDetailsSectionSx, ...(!editable && transportReadOnlySx) }}>
          <Location
            doctorsList={doctorsList}
            isLoading={doctorsLoading}
            formState={formState}
            setFields={(field, value) => {
              if (field === FIELDS.PICKUP_LOCATION_TYPE && value === PICKUP_TYPE_HOME) {
                setFields({ [field]: value, [FIELDS.PICKUP_DOCTOR_ID]: null });
              } else {
                setFields({ [field]: value });
              }
            }}
            labelText="Pickup Location"
            locationTypeField={FIELDS.PICKUP_LOCATION_TYPE}
            doctorIdField={FIELDS.PICKUP_DOCTOR_ID}
            refetchDoctors={() => setDoctorsRefetchTrigger((prev) => prev + 1)}
            disableToggle
            toggleTooltip={locationTypeTooltip}
            disableSelection={!editable}
            detailsMode
          />
          <Divider />
          <Location
            doctorsList={doctorsList}
            isLoading={doctorsLoading}
            formState={formState}
            setFields={(field, value) => {
              if (field === FIELDS.DROPOFF_LOCATION_TYPE && value === PICKUP_TYPE_HOME) {
                setFields({ [field]: value, [FIELDS.DROPOFF_DOCTOR_ID]: null });
              } else {
                setFields({ [field]: value });
              }
            }}
            labelText="Drop Off Location"
            locationTypeField={FIELDS.DROPOFF_LOCATION_TYPE}
            doctorIdField={FIELDS.DROPOFF_DOCTOR_ID}
            refetchDoctors={() => setDoctorsRefetchTrigger((prev) => prev + 1)}
            disableToggle
            toggleTooltip={locationTypeTooltip}
            disableSelection={!editable}
            detailsMode
          />
          <Divider />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography color="text.secondary" sx={getTransportLabelSx(true)}>
              Return Trip:
              <Typography component="span" sx={{ ...getTransportContentSx(true), ml: 1.25 }}>
                {hasLinkedRequest ? 'Yes' : 'No'}
              </Typography>
            </Typography>
            {hasLinkedRequest && (
              <Button
                color="secondary"
                sx={{ fontSize: '14px', whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={() => {
                  // Forward calendarState if present
                  const calendarState = location.state?.calendarState;
                  const path = request.occurrenceDate
                    ? `/dashboard/transports/request-details/${request.linkedRequestId}/${request.occurrenceDate}`
                    : `/dashboard/transports/request-details/${request.linkedRequestId}`;
                  navigate(path, {
                    state: {
                      ...(calendarState ? { calendarState } : {}),
                    },
                  });
                }}
              >
                {request.direction === TRIP_DIRECTION.INBOUND ? 'View Outbound Trip' : 'View Inbound Trip'}
              </Button>
            )}
          </Box>
          <Divider />
          <OutsourceSection
            outsourceState={{
              isOutsourced: formState.IsOutsourced,
              outsourceCompanyId: formState.OutsourceCompanyId,
              vehicleId: formState.VehicleId,
              assignedDriverId: formState.AssignedDriverId,
              facilityId: formState.FacilityId,
              transportType: formState.TransportTypeName,
            }}
            setIsOutsourced={(v) => {
              setFields({
                [FIELDS.IS_OUTSOURCED]: v,
                [FIELDS.OUTSOURCE_COMPANY_ID]: null,
                [FIELDS.VEHICLE_ID]: null,
                [FIELDS.ASSIGNED_DRIVER_ID]: null,
              });
            }}
            setter={setSingleField}
            disabled={!editable}
            showTransportType
            detailsMode
          />
          <Divider />
          <AppointmentSection
            hasExistingAppointment={formState.HasExistingAppointment}
            setHasExistingAppointment={(v) => {
              if (v === true) {
                setFields({ [FIELDS.HAS_EXISTING_APPOINTMENT]: v, [FIELDS.SEEN_BY_DATE]: null });
              } else if (v === false) {
                setFields({ [FIELDS.HAS_EXISTING_APPOINTMENT]: v, [FIELDS.APPOINTMENT_DATE_TIME]: null });
              }
            }}
            pickupTime={formState.PickupTime}
            appointmentDateTime={formState.AppointmentDateTime}
            setter={setSingleField}
            seenByDate={formState.SeenByDate}
            setSeenByDate={(v) => setFields({ [FIELDS.SEEN_BY_DATE]: v })}
            restrictedDates={restrictedDates}
            restrictedDatesLoading={restrictedDatesLoading}
            setIsAllowedPickupDate={setIsAllowedPickupDate}
            setIsAllowedAppointmentDate={setIsAllowedAppointmentDate}
            FIELDS={FIELDS}
            showAllWithoutToggle
            viewOnly={!editable}
          />
          <Divider />
          <RecurrenceRules
            formState={formState}
            setFields={setFields}
            disabled={!editable}
            isNewRequest={false}
            showToggle={!wasRecurring}
            detailsMode
          />
          <Divider />
          <EscortDetails
            aideRequired={formState.AideRequired}
            unitAideRequired={formState.UnitAideRequired}
            cnaRequired={formState.CNARequired}
            setter={setSingleField}
            disabled={!editable}
            detailsMode
          />
          <Divider />
          <AppointmentDescription
            setFields={(e) => setFields({ [FIELDS.APPOINTMENT_DESC]: e.target.value })}
            multiline={false}
            value={formState.AppointmentDesc}
            disabled={!editable}
            detailsMode
          />
          <Divider />
          <AdditionalDetails
            details={formState}
            setDetail={setSingleField}
            labelBoxSx={{ pl: 0, pt: 1, minWidth: 130 }}
            disabled={!editable}
            detailsMode
          />
        </Grid>
      </Grid>
    </>
  );
}
